import { centsToDollars } from '@core/util/money';
import {
	getMortgageLenderNamesForTitleInsurance,
	getMortgageLenders,
	getPriorityNumber,
} from '@core/util/mortgage';
import { getCondoUnitLegalDescription } from '@core/util/parcels';
import { isSet } from '@core/util/value';
import { requisitions } from '@dbData/requisitions.data';
import { RepresentedByBroker } from '@fnModels/chicago';
import { Address } from '@models/address';
import { BusinessAddress } from '@models/businesses';
import {
	BaseClient,
	ClientType,
	PersonClient,
	RepresentationType,
} from '@models/clients';
import { Encumbrance, EncumbranceActionType } from '@models/encumbrance';
import { ComplianceRequest } from '@models/jurisdictions';
import { LawFirm, LawFirmEmployee } from '@models/lawFirms';
import {
	ActingForType,
	Brokerage,
	BusinessMatterClientWithAssociations,
	CondoUnit,
	CondominiumType,
	DepositHolder,
	InterestOptions,
	MatterFileType,
	MatterPropertyDetails,
	MatterPropertyType,
	MatterTitle,
	PersonMatterClientWithAssociations,
	PropertyStatus,
	PropertyType,
	PropertyUnitType,
	RegistrationMethodType,
} from '@models/matter';
import {
	MatterMortgage,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { CorrespondenceType, Lender, MortgageeType } from '@models/lender';
import {
	CloserDefaultRequisitionPrecedent,
	MatterRequisitionPrecedent,
	RequisitionPrecedentIds,
} from '@models/requisitions';
import {
	ChicagoFirmAddress,
	ExistingUninsuredMortgages,
	Firm,
	Mortgage,
	PolicyType,
	Property,
	PropertyIdentifier,
	TransactionType,
} from '@models/title-insurance/chicago';
import { cloneDeep } from 'lodash';

export const GetProperty = (
	fileType: MatterFileType,
	propertyDetails: MatterPropertyDetails,
	brokerage: Brokerage,
	vendorBrokerage: Brokerage,
	vendors: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
	encumbrances: Encumbrance[],
	complianceRequests: ComplianceRequest[],
	_requisitions: MatterRequisitionPrecedent[],
	mortgages: MatterMortgage[],
	matterTitles: MatterTitle[],
): Property => {
	const result: Property = {
		Address1: GetPropertyAddress(
			propertyDetails?.address?.street,
			propertyDetails?.address?.poBox,
		),
		Address2: propertyDetails?.address?.unit,
		PostalCode: propertyDetails?.address?.postcode,
		Province: propertyDetails?.address?.province,
		City: propertyDetails?.address?.city,
		PropertyIdentifiers: GetPropertyIdentifiers(propertyDetails, matterTitles),
		PriorMortgagesOnTitle: GetPriorMortgagesOnTitle(
			encumbrances,
			fileType,
			_requisitions,
			mortgages,
		), // this needs researched
		PropertyType: GetPropertyType(propertyDetails?.propertyType),
		AssessmentRollNumber: propertyDetails?.rollNumber,
		InterestHeld: GetInterestHeld(propertyDetails?.interestEstate),
		RealEstateTaxes: complianceRequests.length > 0 ? 1 : 0,
	};

	if (fileType === MatterFileType.PURCHASE) {
		result.PartyRepresentedByBroker = GetPartyRepresentedByBroker(
			brokerage,
			vendors,
			vendorBrokerage,
		);
	}

	return result;
};

export const GetPropertyAddress = (
	streetAddress: string,
	poBox: string,
): string => [streetAddress, poBox].filter(Boolean).join(', ');

export const GetPartyRepresentedByBroker = (
	brokerage: Brokerage,
	vendors: BaseClient[],
	vendorBrokerage: Brokerage,
): number => {
	if (brokerage || vendorBrokerage) {
		return RepresentedByBroker.YES;
	}

	const builderExists = vendors?.some((x) => x.type === ClientType.BUILDER);

	if (builderExists) {
		return RepresentedByBroker.BUYING_FROM_BUILDER;
	}

	return RepresentedByBroker.NO;
};

export const GetFirm = (
	businessDefaultChicagoName: string,
	office: BusinessAddress,
	primaryOfficeName: string,
	businessAddress: Address,
): Firm => ({
	Name: GetFirmName(
		businessDefaultChicagoName,
		primaryOfficeName,
		office?.chicagoFirmName,
	),
	Address: getAddress(office, businessAddress),
});

const getAddress = (
	office: BusinessAddress,
	businessAddress: Address,
): ChicagoFirmAddress => {
	if (office && isSet(office.chicagoFirmName) && isSet(office.street)) {
		return {
			Address1: office?.street,
			Address2: office?.street2,
			Province: office?.province,
			City: office?.city,
			PostalCode: office?.postcode,
		};
	}

	return {
		Address1: businessAddress?.street,
		Address2: businessAddress?.street2,
		Province: businessAddress?.province,
		City: businessAddress?.city,
		PostalCode: businessAddress?.postcode,
	};
};

const GetFirmName = (
	businessDefaultChicagoName: string,
	primaryOfficeName: string,
	matterOfficeNameOverride: string,
) => {
	if (isSet(matterOfficeNameOverride)) {
		return matterOfficeNameOverride;
	}

	if (isSet(businessDefaultChicagoName)) {
		return businessDefaultChicagoName;
	}

	return primaryOfficeName;
};

export const GetExistingUninsuredMortgages = (
	encumbrances: Encumbrance[],
): ExistingUninsuredMortgages[] => {
	const remain = encumbrances?.filter(
		(x) => x.action === EncumbranceActionType.REMAIN,
	);

	if (!remain || remain?.length === 0) {
		return [];
	}

	return remain.map((x) => {
		const mortgage: ExistingUninsuredMortgages = {
			LenderName:
				x.mortgageeType === MortgageeType.PRIVATE_LENDER
					? x.privateLender?.fullName
					: x.financialInstitution?.name,
			Priority: x.priorityAfterClosing,
			RegisteredAmount: Number(centsToDollars(x.outstandingAmount)),
		};

		return mortgage;
	});
};

export const GetPropertyType = (propertyType: MatterPropertyType): number => {
	if (propertyType?.type === PropertyType.HOME) {
		if (propertyType?.multiUnit) {
			switch (propertyType.unitType) {
				case PropertyUnitType.LIVE_WORK:
					return 10;
				case PropertyUnitType.STUDENT_ROOMING:
					return 8;
				case PropertyUnitType.MULTI_FAMILY:
					return 4;
			}
		}

		return propertyType?.status === PropertyStatus.NEW ? 5 : 7;
	}

	if (propertyType?.type === PropertyType.CONDO) {
		if (propertyType?.parcelOfTiedLand) {
			return 13;
		}

		return propertyType?.condoType === CondominiumType.VACANT ? 11 : 1;
	}

	switch (propertyType?.type) {
		case PropertyType.VACANT:
			return 6;
		case PropertyType.FARM_LAND:
			return 2;
		case PropertyType.MOBILE_HOME:
			return 3;
		case PropertyType.OTHER:
			return 9;
	}

	return 0;
};

export const GetInterestHeld = (
	interestEstate: InterestOptions | string,
): number => {
	switch (interestEstate) {
		case InterestOptions.FEES:
			return 1;
		case InterestOptions.SURFRT:
			return 2;
		case InterestOptions.LHOLD:
			return 3;
		default:
			return 4;
	}
};

export const GetMortgages = (
	mortgages: MatterMortgageWithAssociations[],
	ownerOccupied: boolean,
	closingDate: string,
): Mortgage[] => {
	const resultMortgages = mortgages.map((mortgage) => {
		const LenderName = getMortgageLenderNamesForTitleInsurance(mortgage);

		const _mortgage: Mortgage = {
			ReferenceNumber: mortgage.referenceNumber,
			Amount: Number(centsToDollars(mortgage.amount)),
			LenderName: LenderName,
			Priority: getPriorityNumber(mortgage.priority),
			ClosingDate: closingDate,
			Type: GetMortgageType(mortgage),
			OwnerOccupied: ownerOccupied ? 1 : 0,
		};

		return _mortgage;
	});

	return resultMortgages;
};

export const GetMortgageType = (mortgage: MatterMortgage): number => {
	const lenders = getMortgageLenders(mortgage);
	const financialInstitution = lenders.find(
		(lend) => lend.mortgageeType === MortgageeType.INSTITUTION,
	);
	const computerShare = lenders.find(
		(lend) => lend.mortgageeType === MortgageeType.COMPUTERSHARE,
	);

	const privateLender = lenders.find(
		(lend) => lend.mortgageeType === MortgageeType.PRIVATE_LENDER,
	);

	if (privateLender) {
		return 2;
	}

	if (financialInstitution || computerShare) {
		return 1;
	}
	return null;
};

export const getRegistrationMethod = (
	registrationMethod: RegistrationMethodType,
): string => {
	switch (registrationMethod) {
		case RegistrationMethodType.LAND_TITLES:
			return 'LT';
		case RegistrationMethodType.REGISTRY:
			return 'R';
		default:
			return '';
	}
};

export const GetPropertyIdentifiers = (
	propertyDetails: MatterPropertyDetails,
	matterTitles: MatterTitle[],
): PropertyIdentifier[] => {
	const result: PropertyIdentifier[] = [];
	const titles = cloneDeep(matterTitles);
	titles?.sort((a, b) => {
		if (a.primary && !b.primary) {
			return -1;
		}
		if (!a.primary && b.primary) {
			return 1;
		}

		return 0;
	});
	if (propertyDetails?.propertyType?.type !== PropertyType.CONDO) {
		titles?.forEach((title) => {
			if (title.pinNumber) {
				const identifier: PropertyIdentifier = {
					PID: `${title.pinNumber}-${getRegistrationMethod(propertyDetails?.registrationMethod)}`,
				};

				const description = title.legalDescription;
				if (description) {
					// this needs to be tested to see the max length of this field
					identifier.LegalDescription = description;
				}

				result.push(identifier);
			}
		});
	} else {
		const condoIdentifiers = titles
			?.filter((x) => x.pinNumber)
			.map((unit) => {
				const identifier: PropertyIdentifier = {
					PID: `${unit.pinNumber}-${getRegistrationMethod(propertyDetails?.registrationMethod)}`,
					LegalDescription: getCondoUnitLegalDescription(unit as CondoUnit),
				};

				return identifier;
			});

		result.push(...condoIdentifiers);
	}

	const uniqueResult = result.filter(
		(x, i, a) => a.findIndex((y) => y.PID === x.PID) === i,
	);

	return uniqueResult;
};

export const GetPriorMortgagesOnTitle = (
	encumbrances: Encumbrance[],
	fileType: MatterFileType,
	_requisitions: MatterRequisitionPrecedent[],
	mortgages: MatterMortgage[],
): number => {
	// Filter out requisitions that are not applicable to the purchase file type
	if (fileType === MatterFileType.PURCHASE) {
		if (
			_requisitions?.some((x) =>
				[
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE_ASSUMED_AMENDED_TERMS,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE_ASSUMED_EXISTING_TERMS,
					).name,
				].includes(x.name as RequisitionPrecedentIds),
			)
		) {
			return 0;
		}

		if (
			_requisitions?.some((x) =>
				[
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CAUTION_CHARGE,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id ===
							RequisitionPrecedentIds.CAUTION_CHARGE_BANKRUPTCY_AND_INSOLVENCY,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE_POWER_OF_SALE,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE_BY_RELIGIOUS_ORGANIZATION,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE_WITH_TRANSFER_OF_CHARGE,
					).name,
					requisitions.find(
						(r: CloserDefaultRequisitionPrecedent) =>
							r.id === RequisitionPrecedentIds.CHARGE_WITH_POSTPONEMENT,
					).name,
				].includes(x.name as RequisitionPrecedentIds),
			)
		) {
			return 1;
		}

		return 2;
	}
	if (!encumbrances || encumbrances?.length === 0) {
		return 2;
	}

	const hasRemain = encumbrances.some(
		(x) => x.action === EncumbranceActionType.REMAIN,
	);
	const mortgagePriorities = mortgages?.map((x) => x.priority);

	const remainPriorityIsHigher = encumbrances.some(
		(x) =>
			mortgagePriorities?.some((m) => m < x.priority) &&
			x.postponeMortgage &&
			x.action === EncumbranceActionType.REMAIN,
	);

	if (remainPriorityIsHigher) {
		return 2;
	}

	return hasRemain ? 0 : 1;
};

export const GetTransactionType = (
	fileType: MatterFileType,
	mortgages: MatterMortgageWithAssociations[],
): TransactionType => {
	if (fileType === MatterFileType.PURCHASE) {
		return 'Purchase';
	}

	if (mortgages?.some((x) => x?.mms)) {
		return 'Existing Owner Policy';
	}
	return 'Refinance';
};

export const GetPolicyType = (
	fileType: MatterFileType,
	mortgages?: MatterMortgage[],
): PolicyType => {
	if (fileType === MatterFileType.PURCHASE) {
		if (!mortgages || mortgages?.length === 0) {
			return 'Owners';
		}

		const mortgageIsMMS: boolean = mortgages.some((mortgage) => mortgage.mms);

		return mortgageIsMMS ? 'Owners' : 'Both';
	}

	return 'Lenders';
};

export const GetWasDepositPaidToVendor = (
	depositHolder: DepositHolder,
): number => {
	if (
		depositHolder === DepositHolder.PURCHASER_SOLICITOR ||
		depositHolder === DepositHolder.PURCHASE_BROKERAGE
	) {
		return 1;
	}

	return 0;
};

export const GetArePartiesSigningPowerOfAttorney = (
	clients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
): number => {
	const exist = clients.some(
		(x) => (x as PersonClient)?.representation === RepresentationType.ATTORNEY,
	);

	return exist ? 1 : 0;
};

export const GetFirmRepresentParty = (
	fileType: MatterFileType,
	mortgages: MatterMortgage[],
	actingFor?: ActingForType,
): number => {
	if (mortgages.length === 0) {
		if (fileType === MatterFileType.PURCHASE) {
			return 1;
		}

		switch (actingFor) {
			case ActingForType.MORTGAGEE:
				return 2;
			case ActingForType.MORTGAGOR:
				return 1;
			case ActingForType.BOTH_PRIMARY_MORTGAGEE:
				return 3;
			case ActingForType.BOTH_PRIMARY_MORTGAGOR:
				return 3;
			default:
				return 0;
		}
	}

	return mortgages?.some((x) => !x.actingForMortgagee) ? 1 : 3;
};

export const GetLawyerDetails = (
	fileType: MatterFileType,
	actingFor?: ActingForType,
	otherPartyLawyer?: LawFirmEmployee,
	mortgages?: MatterMortgage[],
): string => {
	if (
		fileType === MatterFileType.REFINANCE &&
		actingFor === ActingForType.MORTGAGEE &&
		otherPartyLawyer
	) {
		return CreateLawyerDetailsString(
			otherPartyLawyer.firstName,
			otherPartyLawyer.lastName,
			otherPartyLawyer.phoneNumber,
		);
	}

	if (fileType === MatterFileType.PURCHASE) {
		let lender: Lender;
		const mortgage = mortgages
			.sort((a, b) => a.priority - b.priority)
			.find((m: MatterMortgage) => {
				const lenderId = Object.keys(m.lenders ?? {})?.find(
					(lenderId) =>
						m.lenders?.[lenderId]?.mortgageeType === 'privateLender',
				);
				lender = m.lenders?.[lenderId];
				return (
					lender?.mortgageeType === 'privateLender' &&
					m?.correspondence?.correspondenceType ===
						CorrespondenceType.SOLICITOR &&
					m?.correspondence?.correspondence &&
					!m.actingForMortgagee
				);
			});

		if (mortgage) {
			const correspondence = mortgage.correspondence?.correspondence as LawFirm;
			return CreateLawyerDetailsString(
				correspondence?.lawyer?.firstName,
				correspondence?.lawyer?.lastName,
				correspondence?.lawyer?.phoneNumber,
			);
		}
	}

	return '';
};

export const CreateLawyerDetailsString = (
	_firstName: string,
	_lastName: string,
	_phoneNumber: string,
): string => {
	const firstName = _firstName ?? '';
	const lastName = _lastName ?? '';
	const phoneNumber = _phoneNumber ?? '';

	if (!firstName && !lastName && !phoneNumber) {
		return '';
	}

	let details = `${firstName} ${lastName} ${phoneNumber}`;

	if (details.length > 30) {
		const firstNameInitial = lastName ? firstName.charAt(0) : '';
		const noSlashesPhoneNumber = phoneNumber.replace(/-/g, '');
		const lengthWithoutLastName =
			firstNameInitial.length + noSlashesPhoneNumber.length;
		const newLastName = lastName
			? lastName.substring(0, 28 - lengthWithoutLastName)
			: firstName.substring(0, 28 - lengthWithoutLastName);
		details = `${firstNameInitial} ${newLastName} ${noSlashesPhoneNumber}`;
	}

	details = details.replace('  ', ' ').trim();

	return details ?? '';
};
